import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/common/helpers/utils";

export enum InputVariant {
  Filled = "filled",
  Outlined = "outlined",
}

export const inputVariants = cva(
  cn(
    "focus-indicator-input w-full rounded-md border transition-colors",
    "focus-visible:ring-1 focus-visible:ring-primary/20 focus-visible:outline-none",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ),
  {
    variants: {
      variant: {
        [InputVariant.Filled]: "border-transparent bg-input-background",
        [InputVariant.Outlined]: "border-input-border bg-transparent",
      },
    },
    defaultVariants: {
      variant: InputVariant.Filled,
    },
  },
);

export enum InputTextAlign {
  Left = "left",
  Center = "center",
  Right = "right",
}

function resolveTextAlignmentClass(textAlign: InputTextAlign): string {
  switch (textAlign) {
    case InputTextAlign.Center:
      return "text-center";
    case InputTextAlign.Right:
      return "text-right tabular-nums";
    default:
      return "text-left";
  }
}

function resolveInputSpacingClass(
  type: React.InputHTMLAttributes<HTMLInputElement>["type"],
  textAlign: InputTextAlign,
): string {
  if (type === "number" && textAlign === InputTextAlign.Right) {
    return "input-right-aligned-number";
  }
  return "";
}

enum NumberStepDirection {
  Up = "up",
  Down = "down",
}

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "prefix">, VariantProps<typeof inputVariants> {
  error?: boolean;
  prefix?: React.ReactNode;
  inputClassName?: string;
  textAlign?: InputTextAlign;
  ref?: React.Ref<HTMLInputElement>;
}

const Input = ({
  className,
  type,
  error,
  variant,
  prefix,
  inputClassName,
  textAlign = InputTextAlign.Left,
  disabled,
  readOnly,
  ref,
  ...props
}: InputProps) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const textAlignmentClassName = resolveTextAlignmentClass(textAlign);
  const inputSpacingClassName = resolveInputSpacingClass(type, textAlign);
  const showNumberStepper = type === "number" && textAlign === InputTextAlign.Right;

  function setInputRef(node: HTMLInputElement | null): void {
    inputRef.current = node;

    if (typeof ref === "function") {
      ref(node);
      return;
    }

    if (ref) {
      ref.current = node;
    }
  }

  function stepNumberInput(direction: NumberStepDirection): void {
    const input = inputRef.current;
    if (!input || disabled || readOnly) {
      return;
    }

    const previousValue = input.value;

    try {
      if (direction === NumberStepDirection.Up) {
        input.stepUp();
      } else {
        input.stepDown();
      }
    } catch {
      return;
    }

    if (input.value !== previousValue) {
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }

    input.focus();
  }

  const numberStepper = showNumberStepper ? (
    <div className="text-muted-foreground flex h-7 w-4 shrink-0 flex-col items-center justify-center">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Increase value"
        className="hover:text-foreground focus-visible:text-foreground flex h-3.5 w-4 items-center justify-center rounded-sm transition-colors focus-visible:outline-none disabled:pointer-events-none"
        disabled={disabled || readOnly}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => stepNumberInput(NumberStepDirection.Up)}
      >
        <ChevronUpIcon className="size-3" aria-hidden="true" />
      </button>
      <button
        type="button"
        tabIndex={-1}
        aria-label="Decrease value"
        className="hover:text-foreground focus-visible:text-foreground flex h-3.5 w-4 items-center justify-center rounded-sm transition-colors focus-visible:outline-none disabled:pointer-events-none"
        disabled={disabled || readOnly}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => stepNumberInput(NumberStepDirection.Down)}
      >
        <ChevronDownIcon className="size-3" aria-hidden="true" />
      </button>
    </div>
  ) : null;

  if (prefix != null || showNumberStepper) {
    return (
      <div
        className={cn(
          inputVariants({ variant }),
          "flex h-9 items-center gap-2 px-3",
          "focus-within:ring-primary/20 focus-within:ring-1 focus-within:outline-none",
          disabled ? "cursor-not-allowed opacity-50" : "",
          error ? "ring-error bg-error/5 ring-1" : "",
          className,
        )}
      >
        {prefix != null ? (
          <span aria-hidden="true" className="text-muted-foreground shrink-0 text-sm select-none">
            {prefix}
          </span>
        ) : null}
        <input
          type={type}
          data-focus-indicator="none"
          className={cn(
            "placeholder:text-muted-foreground file:text-foreground min-w-0 flex-1 bg-transparent py-1 text-sm outline-none file:border-0",
            "file:bg-transparent file:text-sm file:font-medium disabled:cursor-not-allowed",
            textAlignmentClassName,
            inputSpacingClassName,
            inputClassName,
          )}
          ref={setInputRef}
          disabled={disabled}
          readOnly={readOnly}
          {...props}
        />
        {numberStepper}
      </div>
    );
  }

  return (
    <input
      type={type}
      className={cn(
        inputVariants({ variant }),
        "flex h-9 px-3 py-1 text-sm",
        "file:text-foreground placeholder:text-muted-foreground file:border-0 file:bg-transparent file:text-sm file:font-medium",
        textAlignmentClassName,
        inputSpacingClassName,
        error && "ring-error bg-error/5 ring-1",
        className,
        inputClassName,
      )}
      ref={setInputRef}
      disabled={disabled}
      readOnly={readOnly}
      {...props}
    />
  );
};
Input.displayName = "Input";

export { Input };
